/**
 * Itinerary Normalizer
 *
 * Unified normalization pipeline that ALL itinerary data passes through
 * before entering React state. Consolidates 15+ scattered band-aid fixes
 * into a single, ordered pipeline.
 *
 * Pipeline stages (executed in order):
 * 1. Field Completeness  — ensure required fields exist
 * 2. Time Normalization  — canonical HH:MM 24h format
 * 3. Duration Normalization — parse and validate durations
 * 4. Hotel Consistency   — enforce same hotel per stop
 * 5. Address Validation  — detect wrong-city addresses
 * 6. Geographic Validation — remove unrealistic day trips
 * 7. Item Filtering      — remove in-flight meals, duplicates, suspicious items
 * 8. Overlap Resolution  — fix time conflicts with 15min buffer
 */

import { parseDurationToMinutes } from './itineraryHelpers.js';
import { parseTimeToMinutes } from './timeUtils.js';
import { KNOWN_CITIES, INTEREST_KEYWORDS } from './itineraryConstants.js';
import { isMismatchedCity, isUnrealisticDayTrip } from './itineraryValidators.js';
import { REGIONAL_MEAL_TIMES, getRegionalMealTimes } from './regionalMealTimes.js';
import { levenshteinDistance } from './destinationConstants.js';

// ============================================
// BASE CITY SANITIZATION
// Strips trailing article/hotel-noise tokens from a baseCity string.
// Guards against LLM reorderings like "The Aspen" → "Aspen, The",
// or wholly-contaminated strings like "Lodge, Auberge".
// ============================================
const TRAILING_NOISE_TOKENS = new Set([
  'the', 'a', 'an',
  'le', 'la', 'les', 'il', 'lo', 'los', 'las', 'el',
  'inn', 'lodge', 'resort', 'hotel', 'villa', 'auberge',
  'chateau', 'château', 'ranch', 'manor', 'estate',
  'residence', 'suites', 'retreat', 'spa', 'club'
].map(s => s.toLowerCase()));

/**
 * Sanitize a raw baseCity string by stripping trailing noise tokens.
 * - "Aspen, The"        → "Aspen"
 * - "The Little Nell, Aspen, The" → "The Little Nell, Aspen"
 * - "Lodge, Auberge"    → fallback (all tokens are noise)
 * - ""/null/undefined   → fallback
 *
 * @param {string} raw - Input baseCity, possibly with trailing noise tokens.
 * @param {string} fallback - Value to return if fully contaminated or empty.
 * @returns {string}
 */
export function sanitizeBaseCity(raw, fallback = 'Unknown') {
  if (!raw || typeof raw !== 'string') return fallback || 'Unknown';
  let parts = raw.split(',').map(p => p.trim()).filter(Boolean);
  if (parts.length === 0) return fallback || 'Unknown';

  // Pop trailing noise tokens
  while (parts.length > 0 && TRAILING_NOISE_TOKENS.has(parts[parts.length - 1].toLowerCase())) {
    parts.pop();
  }

  // If the first (head) segment is still a pure noise token, the whole string is contaminated
  if (parts.length === 0 || TRAILING_NOISE_TOKENS.has(parts[0].toLowerCase())) {
    return fallback || 'Unknown';
  }

  return parts.join(', ');
}

// ============================================
// CITY NAME NORMALIZATION
// Handles "Lisbon, Portugal" vs "Lisbon" vs "Lisbon, PT"
// ============================================
function normalizeCityForComparison(city) {
  if (!city) return '';
  // Take only the first comma-separated part (city name), stripping region/country suffixes
  // "Playa del Carmen, Quintana Roo, Mexico" → "playa del carmen"
  const firstPart = city.split(',')[0];
  return firstPart.toLowerCase()
    .replace(/^(historic center|old town|city center|downtown|central)\s*,?\s*/i, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function areSameCity(city1, city2) {
  if (!city1 || !city2) return false;
  if (city1 === city2) return true; // Fast path for exact match
  const norm1 = normalizeCityForComparison(city1);
  const norm2 = normalizeCityForComparison(city2);
  if (norm1 === norm2) return true;
  // Check if one contains the other (e.g., "Lisbon" in "Greater Lisbon")
  // Guards against false positives: "Rome" ≠ "Jerome", "York" ≠ "New York", "Nice" ≠ "Venice"
  // Require shorter >5 chars AND >50% of longer string length
  if (norm1.length > 5 && norm2.length > 5) {
    const shorter = norm1.length <= norm2.length ? norm1 : norm2;
    const longer = norm1.length > norm2.length ? norm1 : norm2;
    if (shorter.length / longer.length > 0.5 && longer.includes(shorter)) return true;
  }
  return false;
}

// ============================================
// HOTEL NAME COMPARISON
// Handles "Villa San Michele, A Belmond Hotel" vs "Villa San Michele"
// ============================================
function normalizeHotelName(name) {
  if (!name) return '';
  return name.toLowerCase()
    .replace(/[''`]/g, "'")
    .replace(/\s+/g, ' ')
    .replace(/^the\s+/i, '')
    .trim();
}

export function areSameHotel(name1, name2) {
  if (!name1 || !name2) return false;
  if (name1 === name2) return true;

  const norm1 = normalizeHotelName(name1);
  const norm2 = normalizeHotelName(name2);
  if (norm1 === norm2) return true;

  // Strip common suffixes: "& spa", "resort", "hotel", "suites", etc.
  const suffixes = [
    '& spa', '& resort', '& suites', '& hotel', '& casino',
    'spa', 'resort', 'suites', 'hotel', 'casino', 'boutique',
    'collection', 'signature', 'luxury', 'premium', 'grand'
  ];
  let s1 = norm1, s2 = norm2;
  for (const suf of suffixes) {
    s1 = s1.replace(new RegExp(`\\s*${suf}\\s*$`, 'i'), '').trim();
    s2 = s2.replace(new RegExp(`\\s*${suf}\\s*$`, 'i'), '').trim();
  }
  if (s1 === s2 && s1.length > 5) return true;

  // Check containment (e.g., "Villa San Michele" in "Villa San Michele, A Belmond Hotel")
  const shorter = norm1.length <= norm2.length ? norm1 : norm2;
  const longer = norm1.length > norm2.length ? norm1 : norm2;
  if (shorter.length > 10 && shorter.length / longer.length > 0.4) {
    if (longer.startsWith(shorter) || longer.includes(shorter)) return true;
  }

  // Word-overlap scoring (handles "Park Hyatt Tokyo" vs "Park Hyatt Hotel Tokyo")
  const words1 = s1.split(/\s+/).filter(w => w.length > 2);
  const words2 = s2.split(/\s+/).filter(w => w.length > 2);
  if (words1.length >= 2 && words2.length >= 2) {
    const wordsSet2 = new Set(words2);
    const overlap = words1.filter(w => wordsSet2.has(w)).length;
    const maxWords = Math.max(words1.length, words2.length);
    if (overlap / maxWords >= 0.8) return true;
  }

  return false;
}

// ============================================
// LOGGING HELPER
// ============================================

const PREFIX = '[Normalizer]';

// Per-correction structured dumps are useful when debugging but noisy in prod.
// Gate them behind DEBUG_NORMALIZER (env var in Node, globalThis flag in browser).
function isVerboseNormalizer() {
  try {
    if (typeof process !== 'undefined' && process.env && process.env.DEBUG_NORMALIZER) return true;
  } catch (_) { /* no process in browser */ }
  if (typeof globalThis !== 'undefined' && globalThis.DEBUG_NORMALIZER) return true;
  return false;
}

function log(corrections, type, field, item, old, newVal, reason) {
  corrections.push({ type, field, item: item || '', old: old || '', new: newVal || '', reason });
}

// ============================================
// TIME PARSING (handles all formats)
// ============================================

function parseTimeRaw(timeStr) {
  if (!timeStr) return null;
  const str = timeStr.toString().trim().toUpperCase();

  // 12-hour format with AM/PM: "2:30 PM", "11:00AM"
  const match12 = str.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/);
  if (match12) {
    let hours = parseInt(match12[1]);
    const mins = parseInt(match12[2]);
    const period = match12[3];
    if (period === 'AM' && hours === 12) hours = 0;
    if (period === 'PM' && hours !== 12) hours += 12;
    return { hours, mins, hadAmPm: true, original: timeStr };
  }

  // 12-hour format without minutes: "2PM", "9 AM", "11pm"
  const match12Short = str.match(/^(\d{1,2})\s*(AM|PM)$/);
  if (match12Short) {
    let hours = parseInt(match12Short[1]);
    const period = match12Short[2];
    if (period === 'AM' && hours === 12) hours = 0;
    if (period === 'PM' && hours !== 12) hours += 12;
    return { hours, mins: 0, hadAmPm: true, original: timeStr };
  }

  // 24-hour format: "14:30", "09:00", "1:00"
  const match24 = str.match(/^(\d{1,2}):(\d{2})$/);
  if (match24) {
    const hours = parseInt(match24[1]);
    const mins = parseInt(match24[2]);
    return { hours, mins, hadAmPm: false, original: timeStr };
  }

  // Bare number: "14", "9"
  const matchBare = str.match(/^(\d{1,2})$/);
  if (matchBare) {
    return { hours: parseInt(matchBare[1]), mins: 0, hadAmPm: false, original: timeStr };
  }

  return null;
}

function toMinutes(h, m) {
  return h * 60 + (m || 0);
}

function roundToNearest5(totalMinutes) {
  const rounded = Math.round(totalMinutes / 5) * 5;
  return rounded;
}

function minutesToHHMM(minutes) {
  const rounded = roundToNearest5(minutes);
  const h = Math.floor(rounded / 60) % 24;
  const m = rounded % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// ============================================
// STAGE 1: FIELD COMPLETENESS
// ============================================

function ensureFieldCompleteness(day, context, corrections, warnings) {
  // Ensure arrays exist
  if (!Array.isArray(day.schedule)) day.schedule = [];
  if (!Array.isArray(day.meals)) day.meals = [];

  // Ensure baseCity. Fallback chain (best → worst):
  //   1. inferBaseCityFromHotel(day.hotel) — KNOWN_CITIES match on hotel address
  //   2. context.expectedBaseCity — passed by orchestrator from primaryCityHint/stop
  //   3. context.previousDayCity — same-stop continuity
  //   4. day.hotel.name — even without address match, hotel name is more useful than "Unknown"
  //   5. context.destination — trip-level fallback (may be a country)
  // Only use literal "Unknown" when EVERYTHING above is missing — better to
  // show "Day 3 • Italy" than "Day 3 • Unknown" if that's all we know.
  if (!day.baseCity) {
    const inferred = inferBaseCityFromHotel(day.hotel)
      || context.expectedBaseCity
      || context.previousDayCity
      || (day.hotel?.name && day.hotel.name !== 'Hotel' ? day.hotel.name : null)
      || context.destination
      || 'Unknown';
    log(corrections, 'field', 'baseCity', '', '', inferred, 'Missing baseCity, inferred from hotel/destination');
    day.baseCity = inferred;
    if (inferred === 'Unknown') {
      warnings.push({ field: 'baseCity', item: `Day ${context.dayNumber || '?'}`, reason: 'baseCity could not be inferred — all fallback sources were empty' });
    }
  }

  // Deduplicate state/country suffix: "Ocean City, Maryland, Maryland" → "Ocean City, Maryland"
  if (day.baseCity) {
    const parts = day.baseCity.split(',').map(p => p.trim());
    if (parts.length >= 2) {
      const deduped = [parts[0]];
      for (let i = 1; i < parts.length; i++) {
        if (parts[i].toLowerCase() !== parts[i - 1].toLowerCase()) {
          deduped.push(parts[i]);
        }
      }
      const fixed = deduped.join(', ');
      if (fixed !== day.baseCity) {
        log(corrections, 'field', 'baseCity', '', day.baseCity, fixed, 'Deduplicated state/country suffix');
        day.baseCity = fixed;
      }
    }
  }

  // Strip trailing article/hotel-noise tokens from baseCity (e.g., "Aspen, The" → "Aspen",
  // "Lodge, Auberge" → fallback). Guards against LLM reordering "The Aspen" into "Aspen, The".
  if (day.baseCity) {
    // Same fallback chain as the missing-baseCity branch above — never let
    // sanitizeBaseCity drop us to literal "Unknown" if there's a better
    // option (hotel.name, previousDayCity, etc.).
    const hotelName = day.hotel?.name && day.hotel.name !== 'Hotel' ? day.hotel.name : null;
    const fallback = context.expectedBaseCity
      || context.previousDayCity
      || hotelName
      || context.destination
      || 'Unknown';
    const sanitized = sanitizeBaseCity(day.baseCity, fallback);
    if (sanitized !== day.baseCity) {
      log(corrections, 'field', 'baseCity', '', day.baseCity, sanitized,
        'Stripped trailing article/hotel-noise tokens from baseCity');
      day.baseCity = sanitized;
    }
  }

  // Enforce expectedBaseCity from travelStruct when AI generated the wrong city
  // This catches cases where AI generates "Tokyo" content for a day that should be "Sydney"
  if (context.expectedBaseCity && day.baseCity) {
    const expected = context.expectedBaseCity.toLowerCase().trim();
    const actual = day.baseCity.toLowerCase().trim();
    if (expected && actual && !actual.includes(expected) && !expected.includes(actual)) {
      log(corrections, 'field', 'baseCity', '', day.baseCity, context.expectedBaseCity,
        `baseCity "${day.baseCity}" overridden to match travelStruct expected city`);
      day.baseCity = context.expectedBaseCity;
    }
  }

  // Ensure hotel
  if (!day.hotel) {
    day.hotel = context.expectedHotel || { name: 'Hotel', address: '', pricePerNight: 0 };
    log(corrections, 'field', 'hotel', '', '', day.hotel.name, 'Missing hotel');
  }

  // Activities: ensure required fields
  day.schedule.forEach((item, idx) => {
    if (!item.time) {
      const defaultTime = minutesToHHMM(540 + idx * 120); // 09:00, 11:00, 13:00...
      log(corrections, 'field', 'time', item.activity || `Activity ${idx + 1}`, '', defaultTime, 'Missing time');
      item.time = defaultTime;
    }
    if (!item.duration) {
      item.duration = '1 hour';
      log(corrections, 'field', 'duration', item.activity || `Activity ${idx + 1}`, '', '1 hour', 'Missing duration');
    }
    if (!item.activity && !item.name) {
      warnings.push({ field: 'activity', item: `Schedule item ${idx + 1}`, reason: 'Missing activity name' });
    }
  });

  // Meals: ensure required fields
  day.meals.forEach((item, idx) => {
    const mealType = (item.meal || item.type || '').toLowerCase();
    if (!item.time) {
      let defaultTime = '12:30';
      if (mealType.includes('breakfast')) defaultTime = '08:00';
      else if (mealType.includes('lunch')) defaultTime = '12:30';
      else if (mealType.includes('dinner')) defaultTime = '19:00';
      log(corrections, 'field', 'time', item.restaurant || `Meal ${idx + 1}`, '', defaultTime, `Missing time for ${mealType || 'meal'}`);
      item.time = defaultTime;
    }
    if (!item.duration) {
      item.duration = '1 hour';
    }
    if (!item.meal && !item.type) {
      // Infer from time
      const mins = parseTimeRaw(item.time);
      if (mins) {
        const totalMins = toMinutes(mins.hours, mins.mins);
        if (totalMins < 660) item.meal = 'Breakfast';
        else if (totalMins < 960) item.meal = 'Lunch';
        else item.meal = 'Dinner';
      }
    }
  });
}

function inferBaseCityFromHotel(hotel) {
  if (!hotel?.address) return null;
  const addr = hotel.address.toLowerCase();
  for (const city of KNOWN_CITIES) {
    if (addr.includes(city)) return city.charAt(0).toUpperCase() + city.slice(1);
  }
  return null;
}

// ============================================
// STAGE 2: TIME NORMALIZATION
// ============================================

const EVENING_KEYWORDS = ['evening', 'night', 'sunset', 'dinner', 'illumination', 'nightlife',
  'bar hop', 'cocktail', 'pub crawl', 'light show', 'fireworks', 'stargazing'];

const EARLY_MORNING_KEYWORDS = ['sunrise', 'early morning', 'dawn', 'airport', 'flight',
  'departure', 'red-eye', 'check-out', 'checkout', 'arrival', 'arrive', 'train', 'station',
  'gare', 'bus', 'ferry', 'terminal', 'check-in', 'checkin'];

function normalizeAllTimes(day, context, corrections) {
  const mealTimes = getRegionalMealTimes(context.destination || day.baseCity);

  // Normalize activity times
  day.schedule.forEach((item, idx) => {
    const result = normalizeTimeField(item.time, {
      mealType: null,
      activityName: item.activity || '',
      regionalMealTimes: mealTimes,
      itemIndex: idx,
      totalItems: day.schedule.length,
    });
    if (result.corrected) {
      log(corrections, 'time', 'time', item.activity, item.time, result.time, result.reason);
      item.time = result.time;
    } else {
      item.time = result.time; // Still normalize format (e.g., "9:00" → "09:00")
    }
  });

  // Normalize meal times
  day.meals.forEach((item, idx) => {
    const mealType = (item.meal || item.type || '').toLowerCase();
    const result = normalizeTimeField(item.time, {
      mealType,
      activityName: item.restaurant || '',
      regionalMealTimes: mealTimes,
      itemIndex: idx,
      totalItems: day.meals.length,
    });
    if (result.corrected) {
      log(corrections, 'time', 'time', item.restaurant || mealType, item.time, result.time, result.reason);
      item.time = result.time;
    } else {
      item.time = result.time;
    }
  });
}

function normalizeTimeField(timeStr, hints) {
  const parsed = parseTimeRaw(timeStr);
  if (!parsed) {
    return { time: '09:00', corrected: true, reason: 'Unparseable time' };
  }

  let { hours, mins, hadAmPm } = parsed;

  // If already had AM/PM markers, generally trust them — but sanity-check
  // LLMs sometimes output "2:47 AM" or "1:00 AM" when they clearly mean PM
  if (hadAmPm) {
    // If time resolves to 1:00-5:59 AM, verify it makes sense
    if (hours >= 1 && hours <= 5) {
      const activityLower = (hints.activityName || '').toLowerCase();
      const isEarlyMorningActivity = EARLY_MORNING_KEYWORDS.some(kw => activityLower.includes(kw));
      const mealLower = (hints.mealType || '').toLowerCase();
      const isBreakfast = mealLower.includes('breakfast');

      // Breakfast at 1-5 AM with explicit AM marker — AI made a mistake, clamp to regional breakfast
      if (isBreakfast) {
        const bkfStart = hints.regionalMealTimes?.breakfast?.[0] || 7;
        return { time: minutesToHHMM(toMinutes(bkfStart, 30)), corrected: true, reason: `Breakfast at ${hours}:${String(mins).padStart(2, '0')} AM is too early — clamped to ${bkfStart}:30` };
      }

      // If not an early-morning activity/meal, the LLM probably meant PM
      if (!isEarlyMorningActivity) {
        const corrected = hours + 12;
        return { time: minutesToHHMM(toMinutes(corrected, mins)), corrected: true, reason: `AM marker likely wrong: "${hints.activityName || hints.mealType}" at ${hours}:${String(mins).padStart(2, '0')} AM → ${corrected}:${String(mins).padStart(2, '0')} PM` };
      }
    }
    return { time: minutesToHHMM(toMinutes(hours, mins)), corrected: false };
  }

  // If time is >= 10 and <= 23, it's unambiguous 24h format
  // BUT still sanity-check meal types — AI sometimes outputs 14:00 for breakfast
  if (hours >= 10 && hours <= 23) {
    if (hints.mealType) {
      const mt = hints.mealType.toLowerCase();
      const regionalMeals = hints.regionalMealTimes || {};

      // Breakfast should be before ~11:00 (latest regional breakfast end)
      if (mt.includes('breakfast') && hours >= 12) {
        const bkfEnd = regionalMeals.breakfast?.[1] || 10;
        const bkfStart = regionalMeals.breakfast?.[0] || 7;
        return { time: minutesToHHMM(toMinutes(bkfStart, 30)), corrected: true, reason: `Breakfast at ${hours}:${String(mins).padStart(2, '0')} (24h) is too late — clamped to ${bkfStart}:30` };
      }

      // Lunch should be roughly 11:00–15:00
      if (mt.includes('lunch') && (hours >= 17 || hours < 11)) {
        const lunchStart = regionalMeals.lunch?.[0] || 12;
        return { time: minutesToHHMM(toMinutes(lunchStart, 30)), corrected: true, reason: `Lunch at ${hours}:${String(mins).padStart(2, '0')} (24h) is outside lunch window — clamped to ${lunchStart}:30` };
      }

      // Dinner should be roughly 17:00–23:00
      if (mt.includes('dinner') && hours < 17) {
        const dinnerStart = regionalMeals.dinner?.[0] || 18;
        return { time: minutesToHHMM(toMinutes(dinnerStart, 30)), corrected: true, reason: `Dinner at ${hours}:${String(mins).padStart(2, '0')} (24h) is too early — clamped to ${dinnerStart}:30` };
      }
    }
    return { time: minutesToHHMM(toMinutes(hours, mins)), corrected: false };
  }

  // If hours is 0 (midnight), unambiguous
  if (hours === 0) {
    return { time: minutesToHHMM(toMinutes(0, mins)), corrected: false };
  }

  // AMBIGUOUS ZONE: hours 1-9 without AM/PM
  // Apply disambiguation priority:

  const activityLower = (hints.activityName || '').toLowerCase();

  // Priority 1: Meal type signals
  if (hints.mealType) {
    const mt = hints.mealType.toLowerCase();
    if ((mt.includes('dinner') || mt.includes('supper')) && hours >= 1 && hours <= 9) {
      const corrected = hours + 12;
      const mealWindow = hints.regionalMealTimes?.dinner || [18, 21];
      if (corrected >= mealWindow[0] && corrected <= mealWindow[1] + 1) {
        return { time: minutesToHHMM(toMinutes(corrected, mins)), corrected: true, reason: `Dinner at ${hours}:00 → ${corrected}:00 (meal type signal)` };
      }
    }
    if (mt.includes('lunch') && hours >= 1 && hours <= 5) {
      const corrected = hours + 12;
      const mealWindow = hints.regionalMealTimes?.lunch || [11, 14];
      if (corrected >= mealWindow[0] && corrected <= mealWindow[1] + 2) {
        return { time: minutesToHHMM(toMinutes(corrected, mins)), corrected: true, reason: `Lunch at ${hours}:00 → ${corrected}:00 (meal type signal)` };
      }
    }
    if (mt.includes('breakfast')) {
      if (hours >= 6 && hours <= 10) {
        // Breakfast at 6-10 AM is normal, keep as-is
        return { time: minutesToHHMM(toMinutes(hours, mins)), corrected: false };
      }
      if (hours >= 1 && hours <= 5) {
        // Breakfast at 1-5 without AM/PM — AI probably meant a normal breakfast time, clamp to regional start
        const bkfStart = hints.regionalMealTimes?.breakfast?.[0] || 7;
        return { time: minutesToHHMM(toMinutes(bkfStart, 30)), corrected: true, reason: `Breakfast at ${hours}:${String(mins).padStart(2, '0')} is too early — clamped to ${bkfStart}:30` };
      }
    }
  }

  // Priority 2: Activity keyword signals
  if (EVENING_KEYWORDS.some(kw => activityLower.includes(kw))) {
    if (hours >= 1 && hours <= 9) {
      const corrected = hours + 12;
      if (corrected >= 13 && corrected <= 23) {
        return { time: minutesToHHMM(toMinutes(corrected, mins)), corrected: true, reason: `Evening keyword "${activityLower.substring(0, 30)}" at ${hours}:00 → ${corrected}:00` };
      }
    }
  }

  if (EARLY_MORNING_KEYWORDS.some(kw => activityLower.includes(kw))) {
    // Explicitly morning — keep AM
    return { time: minutesToHHMM(toMinutes(hours, mins)), corrected: false };
  }

  // Priority 3: General heuristic for non-breakfast items
  // Hours 1-6 with no signals → almost certainly PM
  if (hours >= 1 && hours <= 6) {
    const corrected = hours + 12;
    return { time: minutesToHHMM(toMinutes(corrected, mins)), corrected: true, reason: `Ambiguous ${hours}:${String(mins).padStart(2, '0')} → ${corrected}:${String(mins).padStart(2, '0')} (hours 1-6 default to PM)` };
  }

  // Hours 7-9: likely AM (morning activities are common)
  return { time: minutesToHHMM(toMinutes(hours, mins)), corrected: false };
}

// ============================================
// STAGE 3: DURATION NORMALIZATION
// ============================================

function normalizeAllDurations(day, corrections) {
  day.schedule.forEach((item) => {
    const minutes = parseDurationToMinutes(item.duration);
    // Transport items (trains, flights, buses, ferries) can legitimately be 10+ hours
    const name = (item.activity || '').toLowerCase();
    const isTransport = name.includes('train') || name.includes('flight') || name.includes('bus') ||
                        name.includes('ferry') || name.includes('depart') || name.includes('travel') ||
                        item.type === 'inter-city-travel' || item.type === 'airport-transit';
    const maxMinutes = isTransport ? 1800 : 480; // 30h for transport, 8h for activities
    if (minutes > maxMinutes) {
      const capDuration = isTransport ? '12 hours' : '2 hours';
      const capMinutes = isTransport ? 720 : 120;
      log(corrections, 'duration', 'duration', item.activity, item.duration, capDuration, `Unrealistically long (>${maxMinutes / 60}h)`);
      item.duration = capDuration;
      item._durationMinutes = capMinutes;
    } else {
      item._durationMinutes = minutes;
    }
  });

  day.meals.forEach((item) => {
    const minutes = parseDurationToMinutes(item.duration || '1 hour');
    if (minutes > 180) { // > 3 hours for a meal
      log(corrections, 'duration', 'duration', item.restaurant, item.duration, '1.5 hours', 'Meal too long (>3h)');
      item.duration = '1.5 hours';
      item._durationMinutes = 90;
    } else {
      item._durationMinutes = minutes;
    }
  });
}

// ============================================
// STAGE 4: HOTEL CONSISTENCY
// ============================================

function enforceHotelConsistency(day, context, corrections) {
  if (context.expectedHotel && day.hotel) {
    // Normalize hotel name for comparison (strip "Resort", "Hotel", extra whitespace)
    const normName = (n) => (n || '').toLowerCase().replace(/\b(resort|hotel|inn|suites?|lodge)\b/gi, '').replace(/\s+/g, ' ').trim();
    const currentNorm = normName(day.hotel.name);
    const expectedNorm = normName(context.expectedHotel.name);

    if (day.hotel.name !== context.expectedHotel.name) {
      // Check if it's the same hotel with a slightly different name
      const isSimilar = currentNorm === expectedNorm ||
        currentNorm.includes(expectedNorm) || expectedNorm.includes(currentNorm);

      if (isSimilar || day.hotel.name !== context.expectedHotel.name) {
        log(corrections, 'hotel', 'hotel.name', `Day ${context.dayNumber}`, day.hotel.name, context.expectedHotel.name, 'Same-stop hotel consistency');
        day.hotel = { ...context.expectedHotel };
      }
    } else {
      // Same name but price/address might differ — enforce consistency
      if (context.expectedHotel.pricePerNight && day.hotel.pricePerNight !== context.expectedHotel.pricePerNight) {
        log(corrections, 'hotel', 'hotel.pricePerNight', `Day ${context.dayNumber}`, day.hotel.pricePerNight, context.expectedHotel.pricePerNight, 'Same-stop price consistency');
        day.hotel.pricePerNight = context.expectedHotel.pricePerNight;
      }
      if (context.expectedHotel.address && day.hotel.address !== context.expectedHotel.address) {
        day.hotel.address = context.expectedHotel.address;
      }
    }
  }
  if (context.expectedBaseCity && day.baseCity !== context.expectedBaseCity) {
    log(corrections, 'hotel', 'baseCity', `Day ${context.dayNumber}`, day.baseCity, context.expectedBaseCity, 'Same-stop baseCity consistency');
    day.baseCity = context.expectedBaseCity;
  }
}

// ============================================
// STAGE 5: ADDRESS VALIDATION
// ============================================

const GENERIC_PLACEHOLDERS = [
  'explore the area', 'free time', 'leisure time', 'explore nearby',
  'walk around', 'rest at hotel', 'free afternoon', 'explore on your own',
  'city walking tour', 'local market', 'neighborhood stroll',
];

function validateAddresses(day, context, corrections, warnings) {
  const baseCity = (day.baseCity || '').toLowerCase();
  const dayTrip = (context.dayTripDestination || '').toLowerCase();

  const checkAddress = (item, itemType) => {
    const address = (item.address || '').toLowerCase();
    const name = (item.activity || item.restaurant || '').toLowerCase();

    // Flag generic placeholders
    if (GENERIC_PLACEHOLDERS.some(p => name.includes(p))) {
      warnings.push({ field: 'name', item: name, reason: 'Generic placeholder activity' });
    }

    // Check if address mentions a known city that is NOT baseCity or dayTripDestination
    if (address && baseCity) {
      for (const city of KNOWN_CITIES) {
        if (address.includes(city) && !baseCity.includes(city) && city !== dayTrip) {
          // Address mentions a different city — clear it
          log(corrections, 'address', 'address', item.activity || item.restaurant, item.address, '', `Address mentions "${city}" but day is in "${baseCity}"`);
          item.address = '';
          break;
        }
      }

      // Day-trip specific: if day has a day trip and meal address mentions base city
      // but the meal's context suggests it's at the day trip destination
      if (dayTrip && address.includes(baseCity) && !address.includes(dayTrip)) {
        // Could be a wrong-city address for a day-trip meal
        // Only clear if there are day-trip activities suggesting this meal is at the trip destination
        const hasDayTripActivities = (day.schedule || []).some(a => {
          const loc = (a.location || a.activity || '').toLowerCase();
          return loc.includes(dayTrip);
        });
        if (hasDayTripActivities) {
          log(corrections, 'address', 'address', item.activity || item.restaurant, item.address, '', `Day trip to "${dayTrip}" but address is in "${baseCity}"`);
          item.address = '';
        }
      }
    }
  };

  (day.schedule || []).forEach(item => checkAddress(item, 'activity'));
  (day.meals || []).forEach(item => checkAddress(item, 'meal'));
}

// ============================================
// STAGE 6: GEOGRAPHIC VALIDATION
// ============================================

function validateGeography(day, context, corrections) {
  const baseCity = (day.baseCity || '').toLowerCase();

  // Filter schedule items
  day.schedule = day.schedule.filter(item => {
    if (isUnrealisticDayTrip(item, baseCity)) {
      log(corrections, 'geography', 'removed', item.activity, '', '', 'Unrealistic day trip');
      return false;
    }
    if (isMismatchedCity(item, baseCity)) {
      log(corrections, 'geography', 'removed', item.activity, '', '', `Wrong city (base: ${baseCity})`);
      return false;
    }
    return true;
  });

  // Filter meal items
  day.meals = day.meals.filter(item => {
    if (isMismatchedCity({ activity: item.restaurant, location: item.address || item.restaurant }, baseCity)) {
      log(corrections, 'geography', 'removed', item.restaurant, '', '', `Wrong city meal (base: ${baseCity})`);
      return false;
    }
    return true;
  });
}

// ============================================
// STAGE 7: ITEM FILTERING
// ============================================

function filterInvalidItems(day, context, corrections) {
  // 7a. Remove in-flight meals — from BOTH meals[] and schedule[]. The
  // Kyoto/Hunter Valley PDF showed "Airline Catering" (1 hour, Included,
  // International, In Flight) as a standalone timeline activity — the LLM
  // emitted it as a schedule item, not a meal. Scrub both arrays so it
  // stops leaking through.
  const isInFlightName = (name) => {
    const n = String(name || '').toLowerCase();
    return n.includes('in-flight') || n.includes('inflight') ||
      n.includes('onboard') || n.includes('on-board') || n.includes('on board') ||
      n.includes('airplane') || n.includes('airline meal') ||
      n.includes('airline catering') || n.includes('airline food') ||
      n.includes('flight meal') || n.includes('in flight') ||
      n === 'airline catering';
  };
  day.meals = day.meals.filter(meal => {
    const name = meal.restaurant || meal.meal || '';
    if (isInFlightName(name)) {
      log(corrections, 'filter', 'removed', String(name).toLowerCase(), '', '', 'In-flight meal');
      return false;
    }
    return true;
  });
  if (Array.isArray(day.schedule)) {
    day.schedule = day.schedule.filter(item => {
      const name = item.activity || item.name || '';
      const loc = item.location || item.address || '';
      if (isInFlightName(name) || (isInFlightName(loc) && /in\s*flight/i.test(String(loc).toLowerCase()))) {
        log(corrections, 'filter', 'removed', String(name), '', '', 'In-flight activity (not a ground stop)');
        return false;
      }
      return true;
    });
  }

  // 7b. On last day, remove airport/terminal meals
  if (context.isLastDay) {
    day.meals = day.meals.filter(meal => {
      const name = (meal.restaurant || '').toLowerCase();
      const addr = (meal.address || '').toLowerCase();
      if (name.includes('terminal') || name.includes('airport') ||
          addr.includes('terminal') || addr.includes('airport') ||
          name.includes('lounge') || name.includes('aeropuerto') ||
          addr.includes('departure') || addr.includes('arrival')) {
        log(corrections, 'filter', 'removed', meal.restaurant, '', '', 'Airport meal on departure day');
        return false;
      }
      return true;
    });
  }

  // 7b2. Sanitize hotel name — remove departure-related text that the LLM might put in hotel field
  if (day.hotel?.name) {
    const hotelName = day.hotel.name.toLowerCase();
    const departureKeywords = ['departure to', 'depart to', 'leave for', 'return to', 'fly to', 'flight to', 'heading home', 'going home'];
    if (departureKeywords.some(kw => hotelName.includes(kw))) {
      log(corrections, 'filter', 'removed', day.hotel.name, '', '', 'Departure text in hotel name');
      // Try to preserve the hotel from the previous day via context
      day.hotel.name = context.previousHotel || 'Hotel';
      if (context.previousHotelAddress) {
        day.hotel.address = day.hotel.address || context.previousHotelAddress;
      }
    }
  }

  // 7b4. Hotel-name-as-activity guard. The LLM occasionally emits the day's
  // hotel name AS an activity title with a description belonging to a
  // nearby landmark — Day 3 of a Maldives trip surfaced this with "Four
  // Seasons Resort Maldives at Landaa Giraavaru" rendered as a 45-min
  // activity described as "this striking memorial dedicated to the victims
  // of the 2004 tsunami". The hotel name leaked into both the activity
  // slot AND a tsunami-monument description that belonged to a different
  // card on the same day. Drop any activity whose title fuzzy-matches
  // (Levenshtein ≤ 3 OR exact substring match) the day's hotel name.
  const dayHotelName = (day.hotel?.name || day.hotel?.hotelName || '').trim();
  if (dayHotelName && Array.isArray(day.schedule)) {
    const hotelLower = dayHotelName.toLowerCase();
    day.schedule = day.schedule.filter(item => {
      const itemName = (item.activity || item.name || '').trim();
      if (!itemName) return true;
      const itemLower = itemName.toLowerCase();
      // Skip the obvious hotel check-in / check-out items — those are
      // legitimate.
      if (/check.?in|check.?out|arrive at|depart from/i.test(itemLower)) return true;
      const isExactMatch = itemLower === hotelLower;
      const isSubstring = hotelLower.length > 10 && itemLower.includes(hotelLower);
      const isClose = itemLower.length > 10 && levenshteinDistance(itemLower, hotelLower) <= 3;
      if (isExactMatch || isSubstring || isClose) {
        log(corrections, 'filter', 'removed', itemName, '', '', `Activity title matches day hotel name "${dayHotelName}"`);
        return false;
      }
      return true;
    });
  }

  // 7b3. Strip "(Breakfast|Lunch|Dinner) in <anywhere>" placeholder suffixes
  // from BOTH `activity` and `restaurant`/`meal`/`name` fields.
  //
  // The LLM often emits lazy placeholders like `restaurant: "Dinner In Tokyo"`
  // when it can't think of a real venue. Our replaceGenericMeals post-proc
  // tries to replace these with actual restaurants via Places/LLM fallback,
  // but when both fallbacks fail, the raw placeholder text survives and the
  // frontend renders it as the meal card title (via `title = item.restaurant
  // || item.meal || 'Meal'`). Result: user sees "Dinner In Tokyo" on Day 1
  // of their Tokyo trip — visible, ugly, and obviously AI-generated.
  //
  // Earlier normalizer stages only cleaned `activity`. This stage now also
  // cleans `restaurant`, `meal`, and `name` to bare labels ("Dinner"), which
  // keeps the placeholder italic + "Tap for picks" UX without leaking the
  // city suffix. If a later pass (Places hit, user pick, etc.) resolves a
  // real venue, that overwrites the bare label normally.
  const PLACEHOLDER_MEAL_PATTERN = /^(breakfast|lunch|dinner|brunch|supper)\s+(in|at|near|around)\s+.+$/i;
  const isPlaceholderMealText = (s) => {
    if (!s || typeof s !== 'string') return false;
    return PLACEHOLDER_MEAL_PATTERN.test(s.trim());
  };
  const toBareMealLabel = (s) => {
    const m = PLACEHOLDER_MEAL_PATTERN.exec(s.trim());
    if (!m) return s;
    return m[1].charAt(0).toUpperCase() + m[1].slice(1).toLowerCase();
  };

  // Schedule entries: clean `activity` + `restaurant` + `name` when the raw
  // value looks like a generic "(meal) in/at/near X" string.
  if (Array.isArray(day.schedule)) {
    day.schedule.forEach(item => {
      const activityRaw = (item.activity || '').trim();
      const restaurantRaw = (item.restaurant || '').trim();
      const nameRaw = (item.name || '').trim();

      // Don't rewrite if a real restaurant is already resolved (not a bare
      // meal word). Example: "Dinner at Noma" with restaurant="Noma" is real.
      const restaurantResolved = restaurantRaw &&
        !/^(breakfast|lunch|dinner|brunch|supper)$/i.test(restaurantRaw) &&
        !isPlaceholderMealText(restaurantRaw);

      if (isPlaceholderMealText(activityRaw) && !restaurantResolved) {
        const bare = toBareMealLabel(activityRaw);
        log(corrections, 'normalize', 'placeholder-meal-city-strip', activityRaw, '', bare, 'Stripped city suffix from schedule.activity');
        item.activity = bare;
      }
      if (isPlaceholderMealText(restaurantRaw)) {
        const bare = toBareMealLabel(restaurantRaw);
        log(corrections, 'normalize', 'placeholder-meal-city-strip', restaurantRaw, '', bare, 'Stripped city suffix from schedule.restaurant');
        item.restaurant = bare;
      }
      if (isPlaceholderMealText(nameRaw)) {
        const bare = toBareMealLabel(nameRaw);
        log(corrections, 'normalize', 'placeholder-meal-city-strip', nameRaw, '', bare, 'Stripped city suffix from schedule.name');
        item.name = bare;
      }
    });
  }

  // Meals array: same pass on the parallel meals[] collection.
  if (Array.isArray(day.meals)) {
    day.meals.forEach(m => {
      const restaurantRaw = (m.restaurant || '').trim();
      const mealRaw = (m.meal || '').trim();
      const nameRaw = (m.name || '').trim();
      if (isPlaceholderMealText(restaurantRaw)) {
        const bare = toBareMealLabel(restaurantRaw);
        log(corrections, 'normalize', 'placeholder-meal-city-strip', restaurantRaw, '', bare, 'Stripped city suffix from meals[].restaurant');
        m.restaurant = bare;
      }
      if (isPlaceholderMealText(mealRaw)) {
        const bare = toBareMealLabel(mealRaw);
        log(corrections, 'normalize', 'placeholder-meal-city-strip', mealRaw, '', bare, 'Stripped city suffix from meals[].meal');
        m.meal = bare;
      }
      if (isPlaceholderMealText(nameRaw)) {
        const bare = toBareMealLabel(nameRaw);
        log(corrections, 'normalize', 'placeholder-meal-city-strip', nameRaw, '', bare, 'Stripped city suffix from meals[].name');
        m.name = bare;
      }
    });
  }

  // 7b5. Cuisine-carryover guard. The LLM occasionally tags a meal with a
  // cuisine that belongs to a DIFFERENT country than the day's location —
  // surfaced on the Maldives → Vietnam UAT where a Six Senses Ninh Van Bay
  // (Vietnam) breakfast was tagged as "International & Maldivian" cuisine.
  // The Maldivian tag bled in from the prior leg.
  //
  // Strategy: a curated map of niche cuisines (those rarely served outside
  // their home country) → expected country names. If the meal's cuisine is
  // niche-mapped AND the day's location doesn't match the expected country,
  // strip the cuisine field. International cuisines (Italian, French,
  // Japanese, etc.) served everywhere are NOT in the map and pass through.
  const NICHE_CUISINE_TO_COUNTRY = {
    'maldivian': ['maldives'],
    'mongolian': ['mongolia'],
    'tibetan': ['tibet', 'china', 'nepal', 'bhutan'],
    'bhutanese': ['bhutan'],
    'georgian': ['georgia'],
    'azerbaijani': ['azerbaijan'],
    'armenian': ['armenia'],
    'uzbek': ['uzbekistan'],
    'icelandic': ['iceland'],
    'maltese': ['malta'],
    'cypriot': ['cyprus'],
    'sri lankan': ['sri lanka'],
    'burmese': ['myanmar', 'burma'],
    'cambodian': ['cambodia'],
    'laotian': ['laos'],
    'lao': ['laos'],
    'nepalese': ['nepal'],
    'nepali': ['nepal'],
    'mauritian': ['mauritius'],
    'seychellois': ['seychelles'],
    'fijian': ['fiji'],
    'samoan': ['samoa'],
    'tongan': ['tonga'],
    'papua new guinean': ['papua new guinea'],
    'malagasy': ['madagascar'],
  };
  const dayCountry = (day.country || day.baseCountry || context?.country || '').toLowerCase().trim();
  const dayBaseCity = (day.baseCity || day.city || '').toLowerCase();
  const cuisineLeak = (cuisineRaw) => {
    if (!cuisineRaw || typeof cuisineRaw !== 'string') return null;
    const tokens = cuisineRaw.toLowerCase().split(/[,&/]+/).map(t => t.trim()).filter(Boolean);
    for (const token of tokens) {
      const expectedCountries = NICHE_CUISINE_TO_COUNTRY[token];
      if (!expectedCountries) continue;
      // Allow if the day country OR baseCity references one of the expected
      // countries (handles "Maldives, Olhahali Island" baseCity strings).
      const matches = expectedCountries.some(c =>
        dayCountry.includes(c) || dayBaseCity.includes(c)
      );
      if (!matches) return token; // first leaked cuisine wins
    }
    return null;
  };
  if (Array.isArray(day.meals)) {
    day.meals = day.meals.map(meal => {
      const leaked = cuisineLeak(meal.cuisine);
      if (leaked) {
        log(corrections, 'filter', 'cuisine-leak', meal.cuisine, '', '', `"${leaked}" cuisine doesn't match day country (${dayCountry || dayBaseCity || 'unknown'})`);
        const next = { ...meal };
        delete next.cuisine;
        return next;
      }
      return meal;
    });
  }
  if (Array.isArray(day.schedule)) {
    day.schedule = day.schedule.map(item => {
      const leaked = cuisineLeak(item.cuisine);
      if (leaked) {
        log(corrections, 'filter', 'cuisine-leak', item.cuisine, '', '', `"${leaked}" cuisine doesn't match day country (${dayCountry || dayBaseCity || 'unknown'})`);
        const next = { ...item };
        delete next.cuisine;
        return next;
      }
      return item;
    });
  }

  // 7c. Deduplicate schedule vs meals (remove schedule activities that duplicate meals)
  const normalizedMealNames = new Set();
  day.meals.forEach(meal => {
    const name = normalizeRestaurantName(meal.restaurant || meal.meal || '');
    if (name) normalizedMealNames.add(name);
  });

  if (normalizedMealNames.size > 0) {
    day.schedule = day.schedule.filter(item => {
      const actName = normalizeRestaurantName(item.activity || '');
      if (actName && normalizedMealNames.has(actName)) {
        log(corrections, 'filter', 'removed', item.activity, '', '', 'Duplicate of meal entry');
        return false;
      }
      return true;
    });
  }

  // 7d. Filter activities with suspicious times (0:00 - 5:00 AM)
  // By this point, times should already be normalized (Stage 2)
  // If something is still 0-5 AM, it's either genuinely early morning or an error
  day.schedule = day.schedule.filter(item => {
    const parsed = parseTimeRaw(item.time);
    if (!parsed) return true;
    const totalMins = toMinutes(parsed.hours, parsed.mins);
    if (totalMins >= 0 && totalMins < 300) {
      const name = (item.activity || '').toLowerCase();
      const isException = EARLY_MORNING_KEYWORDS.some(k => name.includes(k));
      if (!isException) {
        log(corrections, 'filter', 'removed', item.activity, item.time, '', 'Suspicious time (0-5 AM, not early-morning activity)');
        return false;
      }
    }
    return true;
  });

  // 7e. Filter meals with suspicious times (1:00 - 5:00 AM)
  day.meals = day.meals.filter(meal => {
    const parsed = parseTimeRaw(meal.time);
    if (!parsed) return true;
    const totalMins = toMinutes(parsed.hours, parsed.mins);
    if (totalMins >= 60 && totalMins < 300) {
      log(corrections, 'filter', 'removed', meal.restaurant || meal.meal, meal.time, '', 'Suspicious meal time (1-5 AM)');
      return false;
    }
    return true;
  });

  // 7f. Set-based dedup of activity titles within a day (B1)
  // Removes duplicate cards with the same title anywhere in the day (e.g., two "Overnight Journey" cards)
  if (day.schedule && day.schedule.length > 1) {
    const seenActivities = new Set();
    day.schedule = day.schedule.filter(item => {
      const title = (item.activity || '').toLowerCase().trim();
      if (!title) return true; // Keep items without titles
      if (seenActivities.has(title)) {
        log(corrections, 'filter', 'removed', item.activity, item.time, '', 'Duplicate activity title in same day');
        return false;
      }
      seenActivities.add(title);
      return true;
    });
  }
  // Also dedup meals by restaurant name
  if (day.meals && day.meals.length > 1) {
    const seenMeals = new Set();
    day.meals = day.meals.filter(meal => {
      const key = normalizeRestaurantName(meal.restaurant || meal.meal || '');
      if (!key) return true;
      if (seenMeals.has(key)) {
        log(corrections, 'filter', 'removed', meal.restaurant || meal.meal, meal.time, '', 'Duplicate meal entry');
        return false;
      }
      seenMeals.add(key);
      return true;
    });
  }

  // 7g. Cap or remove large "Free Time" blocks (B2)
  // "Free Time" > 4 hours → remove entirely; 2-4 hours → cap at 2 hours
  const FREE_TIME_PATTERNS = ['free time', 'leisure time', 'explore on your own', 'free afternoon', 'free morning', 'personal time'];
  day.schedule = day.schedule.filter(item => {
    const name = (item.activity || '').toLowerCase();
    const isFreeTime = FREE_TIME_PATTERNS.some(p => name.includes(p));
    if (!isFreeTime) return true;
    const durationMins = item._durationMinutes || parseDurationToMinutes(item.duration) || 60;
    if (durationMins > 240) {
      log(corrections, 'filter', 'removed', item.activity, item.time, '', `Free Time block too large (${durationMins}min)`);
      return false;
    }
    if (durationMins > 120) {
      item.duration = '2 hours';
      item._durationMinutes = 120;
      log(corrections, 'filter', 'duration', item.activity, `${durationMins}min`, '120min', 'Capped Free Time to 2 hours');
    }
    return true;
  });

  // 7h. Improve arrival day (Day 1) schedule quality (B6)
  // Flag hotel restaurant activities as low priority, limit activities if afternoon arrival
  if (context.isFirstDay && context.arrivalTime) {
    const arrParsed = parseTimeRaw(context.arrivalTime);
    if (arrParsed) {
      const arrMins = toMinutes(arrParsed.hours, arrParsed.mins);
      // Flag hotel restaurant meals as low priority
      day.meals.forEach(meal => {
        const name = (meal.restaurant || '').toLowerCase();
        if (name.includes('hotel') || name.includes('lobby') || name.includes('in-house')) {
          meal._lowPriority = true;
        }
      });
      // Afternoon arrival (>= 2 PM) → max 3 activities
      if (arrMins >= 840) { // 14:00
        const maxActivities = 3;
        if (day.schedule.length > maxActivities) {
          const removed = day.schedule.splice(maxActivities);
          removed.forEach(item => {
            log(corrections, 'filter', 'removed', item.activity, item.time, '', 'Afternoon arrival — too many activities');
          });
        }
      }
      // First real activity should be at least 2h after arrival
      const minFirstActivity = arrMins + 120;
      day.schedule = day.schedule.filter(item => {
        const parsed = parseTimeRaw(item.time);
        if (!parsed) return true;
        const itemMins = toMinutes(parsed.hours, parsed.mins);
        if (itemMins < minFirstActivity) {
          // Allow "Check-in" and "Settle In" type activities
          const name = (item.activity || '').toLowerCase();
          if (name.includes('check') || name.includes('settle') || name.includes('arrival') || name.includes('hotel')) return true;
          log(corrections, 'filter', 'removed', item.activity, item.time, '', `Too soon after arrival at ${context.arrivalTime}`);
          return false;
        }
        return true;
      });
    }
  }

  // 7i. Departure day hard cap (B8)
  // On last day, limit activities based on departure time
  if (context.isLastDay && context.departureTime) {
    const depParsed = parseTimeRaw(context.departureTime);
    if (depParsed) {
      const depMins = toMinutes(depParsed.hours, depParsed.mins);

      // Helper: detect transport/departure items that should NOT be filtered
      const isTransportItem = (item) => {
        const name = (item.activity || '').toLowerCase();
        return name.includes('train') || name.includes('flight') || name.includes('bus') ||
               name.includes('ferry') || name.includes('depart') || name.includes('airport') ||
               name.includes('station') || name.includes('check-out') || name.includes('checkout') ||
               item.type === 'inter-city-travel' || item.type === 'airport-transit';
      };

      // D7: Remove activities that are too long (> 90 min) on departure day
      // But EXEMPT transport items (train, flight, etc.) — they ARE the departure
      day.schedule = day.schedule.filter(item => {
        if (isTransportItem(item)) return true; // Never remove departure transport
        const durationMins = item._durationMinutes || parseDurationToMinutes(item.duration) || 60;
        if (durationMins > 90) {
          log(corrections, 'filter', 'removed', item.activity, item.time, '', `Long activity (${durationMins}min) on departure day — max 90min`);
          return false;
        }
        return true;
      });
      // Remove activities starting after departureTime - 4 hours (need time to get to airport)
      // But EXEMPT transport items
      const cutoffMins = depMins - 240;
      if (cutoffMins > 0) {
        day.schedule = day.schedule.filter(item => {
          if (isTransportItem(item)) return true; // Never remove departure transport
          const parsed = parseTimeRaw(item.time);
          if (!parsed) return true;
          const itemMins = toMinutes(parsed.hours, parsed.mins);
          if (itemMins >= cutoffMins) {
            log(corrections, 'filter', 'removed', item.activity, item.time, '', `Too close to departure at ${context.departureTime}`);
            return false;
          }
          return true;
        });
      }
      // D7: Proportional cap: flight after 6 PM → 1 activity; before 2 PM → breakfast only
      // Transport items are exempt from the cap — they ARE the departure
      let maxActivities;
      if (depMins >= 1080) { // 18:00+
        maxActivities = 1; // D7: was 2, now 1 — keep departure day light
      } else if (depMins >= 840) { // 14:00-18:00
        maxActivities = 1;
      } else { // Before 14:00
        maxActivities = 0; // breakfast only
      }
      // Separate transport items from regular activities before applying cap
      const transportItems = day.schedule.filter(item => isTransportItem(item));
      const regularItems = day.schedule.filter(item => !isTransportItem(item));
      if (regularItems.length > maxActivities) {
        const removed = regularItems.splice(maxActivities);
        removed.forEach(item => {
          log(corrections, 'filter', 'removed', item.activity, item.time, '', `Departure day cap (max ${maxActivities} activities)`);
        });
      }
      // Recombine: regular (capped) + transport (preserved)
      day.schedule = [...regularItems, ...transportItems];
      day.schedule.sort((a, b) => {
        const pa = parseTimeRaw(a.time), pb = parseTimeRaw(b.time);
        if (!pa || !pb) return 0;
        return toMinutes(pa.hours, pa.mins) - toMinutes(pb.hours, pb.mins);
      });
    }
  }

  // 7j. Overnight arrival day enforcer (B3 normalizer part)
  // After overnight travel, remove early-morning activities and cap late activities
  if (context.isOvernightArrival) {
    // Remove all activities before 11:00 AM (jet lag recovery)
    day.schedule = day.schedule.filter(item => {
      const parsed = parseTimeRaw(item.time);
      if (!parsed) return true;
      const itemMins = toMinutes(parsed.hours, parsed.mins);
      if (itemMins < 660) { // 11:00
        const name = (item.activity || '').toLowerCase();
        // Allow check-in / settle in / hotel activities
        if (name.includes('check') || name.includes('settle') || name.includes('arrival') || name.includes('hotel')) return true;
        log(corrections, 'filter', 'removed', item.activity, item.time, '', 'Too early after overnight arrival');
        return false;
      }
      return true;
    });
    // Cap the day at 9 PM (jet lag recovery)
    day.schedule = day.schedule.filter(item => {
      const parsed = parseTimeRaw(item.time);
      if (!parsed) return true;
      const itemMins = toMinutes(parsed.hours, parsed.mins);
      if (itemMins >= 1260) { // 21:00
        log(corrections, 'filter', 'removed', item.activity, item.time, '', 'Too late on overnight arrival day');
        return false;
      }
      return true;
    });
  }
}

function normalizeRestaurantName(name) {
  if (!name) return '';
  return name.toLowerCase()
    .replace(/^(lunch at|dinner at|breakfast at|meal at)\s+/i, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// ============================================
// STAGE 8: OVERLAP RESOLUTION
// ============================================

function resolveOverlaps(day, corrections) {
  // Merge all items into a unified list
  const allItems = [];

  (day.schedule || []).forEach((item, idx) => {
    const parsed = parseTimeRaw(item.time);
    const startMins = parsed ? toMinutes(parsed.hours, parsed.mins) : 540;
    allItems.push({
      _ref: item,
      _type: 'activity',
      _originalIndex: idx,
      _name: item.activity || `Activity ${idx}`,
      _startMins: startMins,
      _durationMins: item._durationMinutes || parseDurationToMinutes(item.duration) || 60,
    });
  });

  (day.meals || []).forEach((item, idx) => {
    const parsed = parseTimeRaw(item.time);
    const startMins = parsed ? toMinutes(parsed.hours, parsed.mins) : 720;
    allItems.push({
      _ref: item,
      _type: 'meal',
      _originalIndex: idx,
      _name: item.restaurant || item.meal || `Meal ${idx}`,
      _startMins: startMins,
      _durationMins: item._durationMinutes || parseDurationToMinutes(item.duration || '1 hour') || 60,
    });
  });

  // Sort by start time, D9: when same time, put activities before meals
  // so the activity keeps its slot and the meal gets shifted after it
  allItems.sort((a, b) => {
    if (a._startMins !== b._startMins) return a._startMins - b._startMins;
    // Same start time: activities first, meals second (meals will get shifted)
    if (a._type === 'activity' && b._type === 'meal') return -1;
    if (a._type === 'meal' && b._type === 'activity') return 1;
    return 0;
  });

  // Walk through and resolve overlaps
  // B5 + B9A: Location-aware minimum gaps with airport transfer floor
  const BUFFER_SAME_LOCATION = 15; // 15 min for same venue / nearby
  const BUFFER_CROSS_LOCATION = 30; // 30 min for different locations (need transit)
  const BUFFER_AIRPORT = 45; // 45 min for airport transfers
  const MAX_TIME = 23 * 60 + 45; // 23:45 — never push past this to avoid midnight wrap
  for (let i = 1; i < allItems.length; i++) {
    const prev = allItems[i - 1];
    const prevEnd = prev._startMins + prev._durationMins;
    // Determine if locations differ (cross-location needs more transit time)
    const prevAddr = (prev._ref.address || prev._ref.location || '').toLowerCase();
    const currAddr = (allItems[i]._ref.address || allItems[i]._ref.location || '').toLowerCase();
    const prevName = (prev._name || '').toLowerCase();
    const currName = (allItems[i]._name || '').toLowerCase();
    // B9A: Detect airport transfers
    const AIRPORT_KEYWORDS = ['airport', 'terminal', 'flight', 'check-in', 'departure', 'arrival'];
    const isAirportTransfer = AIRPORT_KEYWORDS.some(kw => prevName.includes(kw) || currName.includes(kw) || prevAddr.includes(kw) || currAddr.includes(kw));
    const sameLocation = !isAirportTransfer && prevAddr && currAddr && (prevAddr === currAddr || prevAddr.includes(currAddr) || currAddr.includes(prevAddr));
    const BUFFER = isAirportTransfer ? BUFFER_AIRPORT : (sameLocation ? BUFFER_SAME_LOCATION : BUFFER_CROSS_LOCATION);
    const minStart = prevEnd + BUFFER;

    if (allItems[i]._startMins < minStart) {
      const oldTime = allItems[i]._ref.time;
      // Cap at 23:45 to prevent wrapping past midnight (which would produce 01:15 AM dinner)
      const cappedStart = Math.min(minStart, MAX_TIME);
      allItems[i]._startMins = cappedStart;
      const newTime = minutesToHHMM(cappedStart);
      allItems[i]._ref.time = newTime;
      log(corrections, 'overlap', 'time', allItems[i]._name, oldTime, newTime,
        cappedStart < minStart
          ? `Overlap with "${prev._name}" — capped at 23:45 (would have been ${minutesToHHMM(minStart)})`
          : `Overlap with "${prev._name}" (ends ${minutesToHHMM(prevEnd)})`);
    }
  }
}

// ============================================
// STAGE 9: LATE-NIGHT ACTIVITY REMOVAL
// Remove activities starting after 22:30 (10:30 PM) and
// outdoor/walking activities starting after 21:30 (9:30 PM).
// Dinner is the only exception (allowed until 22:00 start).
// ============================================

function removeLateNightActivities(day, ctx, corrections) {
  const ABSOLUTE_CUTOFF = 22 * 60 + 30; // 22:30 — no activity starts after this
  const OUTDOOR_CUTOFF = 21 * 60 + 30;  // 21:30 — no outdoor/walking activities after this
  const DINNER_CUTOFF = 22 * 60;         // 22:00 — dinner can start up to here
  const ARRIVAL_DAY_CUTOFF = 21 * 60;    // 21:00 — stricter for arrival days

  const isArrivalDay = ctx.isFirstDay;
  const effectiveCutoff = isArrivalDay ? ARRIVAL_DAY_CUTOFF : ABSOLUTE_CUTOFF;

  // Outdoor/walking activity keywords — these are unsafe or impractical after dark
  const OUTDOOR_KEYWORDS = [
    'walk', 'stroll', 'hike', 'explore', 'wander', 'promenade', 'tour',
    'sunset', 'beach', 'park', 'garden', 'riverside', 'waterfront',
    'viewpoint', 'lookout', 'historic center', 'old town', 'market',
    'square', 'plaza', 'harbor', 'pier', 'boardwalk', 'trail',
    'neighborhood', 'district', 'quarter', 'cycling', 'bike',
    'snorkeling', 'diving', 'kayak', 'surf', 'swim', 'outdoor',
  ];

  // Filter schedule (activities)
  day.schedule = (day.schedule || []).filter(item => {
    const parsed = parseTimeRaw(item.time);
    if (!parsed) return true;
    const startMins = toMinutes(parsed.hours, parsed.mins);
    const name = (item.activity || '').toLowerCase();

    // Check if this is an outdoor/walking activity
    const isOutdoor = OUTDOOR_KEYWORDS.some(k => name.includes(k));

    if (startMins >= effectiveCutoff) {
      log(corrections, 'late-night', 'removed', item.activity, item.time, '',
        `Activity starts at ${item.time} (after ${isArrivalDay ? '9:00 PM arrival day' : '10:30 PM'} cutoff)`);
      return false;
    }

    if (isOutdoor && startMins >= OUTDOOR_CUTOFF) {
      log(corrections, 'late-night', 'removed', item.activity, item.time, '',
        `Outdoor activity "${item.activity}" starts at ${item.time} (after 9:30 PM outdoor cutoff)`);
      return false;
    }

    return true;
  });

  // Filter meals — only remove if starting after 22:30 (dinner up to 22:00 is fine)
  day.meals = (day.meals || []).filter(meal => {
    const parsed = parseTimeRaw(meal.time);
    if (!parsed) return true;
    const startMins = toMinutes(parsed.hours, parsed.mins);
    const mealType = (meal.meal || '').toLowerCase();
    const isDinner = mealType.includes('dinner');

    // Dinner gets a pass until 22:00
    const mealCutoff = isDinner ? DINNER_CUTOFF : ABSOLUTE_CUTOFF;
    const arrivalMealCutoff = isArrivalDay && isDinner ? 21 * 60 : effectiveCutoff;
    const finalCutoff = isArrivalDay ? arrivalMealCutoff : mealCutoff;

    if (startMins >= finalCutoff) {
      log(corrections, 'late-night', 'removed', meal.restaurant || meal.meal, meal.time, '',
        `Meal starts at ${meal.time} (after ${isDinner ? '10:00 PM dinner' : '10:30 PM'} cutoff)`);
      return false;
    }

    return true;
  });
}

// ============================================
// STAGE 10: START TIME ENFORCEMENT
// Shifts all activities forward if they start before the user's preferred start time.
// Skips first day (arrival) and last day (departure).
// ============================================

function enforceStartTime(day, ctx, corrections) {
  const preferredStart = ctx.preferredStartTime || '09:00';

  // For Day 1: enforce arrivalTime as earliest start (shift activities earlier if they start too late)
  if (ctx.isFirstDay && ctx.arrivalTime) {
    const arrParsed = parseTimeRaw(ctx.arrivalTime);
    if (arrParsed) {
      const arrMins = toMinutes(arrParsed.hours, arrParsed.mins);
      const allDayItems = [];
      (day.schedule || []).forEach(item => {
        const p = parseTimeRaw(item.time);
        if (p) allDayItems.push({ ref: item, field: 'time', mins: toMinutes(p.hours, p.mins), name: item.activity || 'Activity' });
      });
      (day.meals || []).forEach(item => {
        const p = parseTimeRaw(item.time);
        if (p) allDayItems.push({ ref: item, field: 'time', mins: toMinutes(p.hours, p.mins), name: item.restaurant || 'Meal' });
      });
      if (allDayItems.length > 0) {
        // Only shift MORNING items that are scheduled too late.
        // Evening activities (dinner, nightlife) should NOT be shifted backward.
        // A dinner at 19:00 should stay at 19:00 even if arrival is 05:30.
        const morningItems = allDayItems.filter(item => item.mins < 840); // Before 2 PM = morning/afternoon
        if (morningItems.length > 0) {
          const earliest = morningItems.reduce((m, i) => i.mins < m.mins ? i : m, morningItems[0]);
          // If first morning activity is more than 90 min after arrival, shift only morning items closer
          if (earliest.mins > arrMins + 90) {
            const shift = earliest.mins - arrMins - 60; // Leave a 1h buffer after arrival
            if (shift > 0) {
              morningItems.forEach(item => {
                const newMins = Math.max(item.mins - shift, arrMins + 60);
                const newTime = minutesToHHMM(newMins);
                if (newTime !== item.ref[item.field]) {
                  log(corrections, 'arrival-time', item.field, item.name, item.ref[item.field], newTime,
                    `Shifted -${shift}min to match arrival time ${ctx.arrivalTime}`);
                  item.ref[item.field] = newTime;
                }
              });
            }
          }
        }
      }
    }
    return;
  }

  // B7: Don't skip departure days — enforce preferred start with departure buffer
  // Skip overnight arrival days — they have jet-lag recovery constraints
  if (ctx.isOvernightArrival) return;

  const preferredParsed = parseTimeRaw(preferredStart);
  if (!preferredParsed) return;
  const preferredMins = toMinutes(preferredParsed.hours, preferredParsed.mins);
  const tolerance = 15; // 15-minute tolerance

  // For last day, respect departure buffer but still enforce start time
  let effectivePreferredMins = preferredMins;
  if (ctx.isLastDay && ctx.departureTime) {
    const depParsed = parseTimeRaw(ctx.departureTime);
    if (depParsed) {
      const depMins = toMinutes(depParsed.hours, depParsed.mins);
      // If departure is very early (before preferred start + 3h), use departure-4h as start
      if (depMins < preferredMins + 180) {
        effectivePreferredMins = Math.max(depMins - 240, 420); // At least 7:00 AM
      }
    }
  }

  // Collect all items with their times
  // Exclude transport items (train, flight, etc.) — they have fixed departure times that should not be shifted
  const _isTransportForShift = (item) => {
    const name = (item.activity || '').toLowerCase();
    return name.includes('train') || name.includes('flight') || name.includes('bus') ||
           name.includes('ferry') || name.includes('depart') || name.includes('airport') ||
           name.includes('station') || item.type === 'inter-city-travel' || item.type === 'airport-transit';
  };
  const allItems = [];
  (day.schedule || []).forEach(item => {
    if (_isTransportForShift(item)) return; // Don't shift transport items
    const parsed = parseTimeRaw(item.time);
    if (parsed) allItems.push({ ref: item, field: 'time', mins: toMinutes(parsed.hours, parsed.mins), name: item.activity || 'Activity' });
  });
  (day.meals || []).forEach(item => {
    const parsed = parseTimeRaw(item.time);
    if (parsed) allItems.push({ ref: item, field: 'time', mins: toMinutes(parsed.hours, parsed.mins), name: item.restaurant || item.meal || 'Meal' });
  });

  if (allItems.length === 0) return;

  // Find earliest item
  const earliest = allItems.reduce((min, item) => item.mins < min.mins ? item : min, allItems[0]);

  // B7: Backward shift — if earliest item > 30 min AFTER preferred start, shift schedule earlier
  if (earliest.mins > effectivePreferredMins + 30 && !ctx.isFirstDay) {
    const backShift = earliest.mins - effectivePreferredMins;
    const MAX_BACKWARD_SHIFT = 120; // Cap backward shift at 2 hours to avoid extreme moves
    const actualShift = Math.min(backShift, MAX_BACKWARD_SHIFT);
    allItems.forEach(item => {
      const newMins = Math.max(item.mins - actualShift, effectivePreferredMins);
      const newTime = minutesToHHMM(newMins);
      if (newTime !== item.ref[item.field]) {
        log(corrections, 'start-time', item.field, item.name, item.ref[item.field], newTime,
          `Shifted -${actualShift}min backward to respect ${preferredStart} start time`);
        item.ref[item.field] = newTime;
      }
    });
    return; // After backward shift, don't also try forward shift
  }

  // Forward shift — if earliest is before preferred start (existing logic)
  if (earliest.mins >= effectivePreferredMins - tolerance) return;

  // Calculate shift amount
  const shift = effectivePreferredMins - earliest.mins;
  const MAX_TIME = 23 * 60; // 23:00 cap

  // Shift all items forward
  allItems.forEach(item => {
    const newMins = Math.min(item.mins + shift, MAX_TIME);
    const newTime = minutesToHHMM(newMins);
    if (newTime !== item.ref[item.field]) {
      log(corrections, 'start-time', item.field, item.name, item.ref[item.field], newTime,
        `Shifted +${shift}min to respect ${preferredStart} start time`);
      item.ref[item.field] = newTime;
    }
  });
}

// ============================================
// STAGE 11: PACE-BASED ACTIVITY LIMITS
// Trims activities when a day exceeds the pace limit.
// Prioritizes keeping unique cultural activities over generic "explore" items.
// ============================================

const PACE_LIMITS = {
  relaxed:    { maxActivities: 3, maxMeals: 3, maxTotal: 6 },
  leisurely:  { maxActivities: 3, maxMeals: 3, maxTotal: 6 }, // alias for relaxed
  moderate:   { maxActivities: 4, maxMeals: 3, maxTotal: 7 },
  balanced:   { maxActivities: 4, maxMeals: 3, maxTotal: 7 },
  packed:     { maxActivities: 6, maxMeals: 3, maxTotal: 9 },
  fast:       { maxActivities: 5, maxMeals: 3, maxTotal: 8 },
  'high-energy': { maxActivities: 6, maxMeals: 3, maxTotal: 9 },
};

// Low-priority activity keywords — these get trimmed first
const LOW_PRIORITY_KEYWORDS = [
  'explore', 'walk', 'stroll', 'wander', 'free time', 'leisure',
  'rest', 'relax', 'browse', 'roam', 'neighborhood stroll',
  'explore the area', 'explore on your own', 'self-guided',
];

// High-priority activity keywords — these get kept
const HIGH_PRIORITY_KEYWORDS = [
  'tour', 'museum', 'temple', 'palace', 'castle', 'cathedral',
  'historic', 'gallery', 'monument', 'ruins', 'archaeological',
  'cooking class', 'workshop', 'performance', 'show', 'concert',
  'snorkeling', 'diving', 'safari', 'trek', 'hike',
];

/**
 * Score an activity on a 1-5 priority scale, taking into account:
 *   1. LLM-provided `priority` field (if present, wins)
 *   2. Keyword heuristics (LOW/HIGH baseline → 1/2/3)
 *   3. User interests (ctx.interests) — bump +2 for any match
 *   4. Taste profile tags/cuisines/vibes (ctx.tasteProfile) — bump +1
 *   5. Penalty -1 when interests are provided but none matched
 *
 * Used by pace-limit trimming so activities the user cares about survive.
 *
 * @param {Object} activity — schedule item with {activity|name, description, priority}
 * @param {Object} [ctx] — { interests?: string[], tasteProfile?: object }
 * @returns {number} clamped to [1, 5]
 */
function getActivityPriority(activity, ctx = {}) {
  const name = (activity.activity || activity.name || '').toLowerCase();
  const desc = (activity.description || '').toLowerCase();
  const text = `${name} ${desc}`;

  // 1) LLM-provided priority wins if present (assumed 0-5 scale)
  if (typeof activity.priority === 'number' && activity.priority >= 0) {
    return Math.max(1, Math.min(5, Math.round(activity.priority)));
  }

  // 2) Baseline from keyword heuristics
  let score;
  if (LOW_PRIORITY_KEYWORDS.some(kw => name.includes(kw))) score = 1;
  else if (HIGH_PRIORITY_KEYWORDS.some(kw => name.includes(kw))) score = 3;
  else score = 2;

  // 3) Interest bonus — match ANY user interest
  const interests = Array.isArray(ctx.interests)
    ? ctx.interests.map(s => String(s).toLowerCase())
    : [];
  const matchedInterest = interests.some(interest => {
    const kws = INTEREST_KEYWORDS[interest] || [];
    return kws.some(kw => text.includes(kw));
  });
  if (matchedInterest) score += 2;

  // 4) Taste profile match — tags/cuisines/vibes bump score
  const taste = ctx.tasteProfile || {};
  const tasteTags = [
    ...(Array.isArray(taste.tags) ? taste.tags : []),
    ...(Array.isArray(taste.cuisines) ? taste.cuisines : []),
    ...(Array.isArray(taste.vibes) ? taste.vibes : []),
  ].map(s => String(s).toLowerCase());
  if (tasteTags.length && tasteTags.some(tag => tag && text.includes(tag))) score += 1;

  // 5) Penalty — interests provided but none matched
  if (interests.length && !matchedInterest) score -= 1;

  return Math.max(1, score);
}

function getMealPriority(meal) {
  const type = (meal.meal || meal.type || '').toLowerCase();
  if (type.includes('dinner')) return 3;   // Always keep dinner
  if (type.includes('breakfast')) return 2;
  if (type.includes('lunch')) return 2;
  return 1; // Snack, tea, etc. — trim first
}

// Helper: does an activity sit adjacent (within ~90min) to any meal on the day?
// When multiple candidates tie on priority, we prefer to KEEP gap-filling
// activities (those isolated between meals) and TRIM ones already next to meals.
function isActivityAdjacentToMeal(activity, meals) {
  if (!activity || !Array.isArray(meals) || meals.length === 0) return false;
  const parsed = parseTimeRaw(activity.time);
  if (!parsed) return false;
  const actMins = toMinutes(parsed.hours, parsed.mins);
  for (const meal of meals) {
    const mp = parseTimeRaw(meal.time);
    if (!mp) continue;
    const mealMins = toMinutes(mp.hours, mp.mins);
    if (Math.abs(actMins - mealMins) <= 90) return true;
  }
  return false;
}

// Helper: find the largest inter-meal gap (in minutes) on the day after trim.
// Used to warn that the downstream free-time detector will need to fill a large void.
function largestInterMealGapMinutes(day) {
  if (!Array.isArray(day.meals) || day.meals.length < 2) return 0;
  const times = day.meals
    .map(m => {
      const p = parseTimeRaw(m.time);
      return p ? toMinutes(p.hours, p.mins) : null;
    })
    .filter(t => t !== null)
    .sort((a, b) => a - b);
  let maxGap = 0;
  for (let i = 0; i < times.length - 1; i++) {
    const gap = times[i + 1] - times[i];
    if (gap > maxGap) maxGap = gap;
  }
  return maxGap;
}

function enforceActivityLimits(day, ctx, corrections) {
  const pace = (ctx.preferredPace || 'moderate').toLowerCase();
  const limits = PACE_LIMITS[pace] || PACE_LIMITS.moderate;

  // Reduce limits for arrival/departure days
  let maxActivities = limits.maxActivities;
  let maxMeals = limits.maxMeals;
  if (ctx.isFirstDay || ctx.isLastDay) {
    maxActivities = Math.max(1, maxActivities - 1);
    // Arrival/departure: reduce meals but never below 2 (arrival: lunch+dinner, departure: breakfast+lunch)
    maxMeals = Math.max(2, maxMeals - 1);
  }

  // Fix C: accumulate user-facing trim records onto the day so downstream UI can
  // surface "N more suggestions available for this day" on the day card.
  if (!Array.isArray(day._trimmedForPace)) day._trimmedForPace = [];

  // Trim activities if over limit
  if (day.schedule && day.schedule.length > maxActivities) {
    // Sort by priority, keeping position for equal priority.
    // `ctx` carries interests + tasteProfile so scoring is interest-aware.
    const indexed = day.schedule.map((item, i) => ({
      item,
      index: i,
      priority: getActivityPriority(item, ctx),
      // Fix B: adjacency flag — when priorities tie, prefer trimming items already
      // sitting next to a meal (they're redundant leisure) over isolated items that
      // fill a gap between meals.
      nextToMeal: isActivityAdjacentToMeal(item, day.meals || [])
    }));

    // Always keep first and last activity (anchor the day) — only trim from middle
    const middle = indexed.slice(1, -1);

    // Sort middle by priority ascending (low priority first = trim first).
    // Tiebreaker: prefer trimming activities adjacent to a meal (nextToMeal=true
    // comes before nextToMeal=false). This keeps gap-filling items intact.
    middle.sort((a, b) => {
      if (a.priority !== b.priority) return a.priority - b.priority;
      if (a.nextToMeal !== b.nextToMeal) return a.nextToMeal ? -1 : 1;
      return a.index - b.index;
    });

    // How many to trim from middle
    const trimCount = day.schedule.length - maxActivities;
    const toTrim = middle.slice(0, trimCount);
    const trimIndices = new Set(toTrim.map(t => t.index));

    // Log and filter
    toTrim.forEach(t => {
      const name = t.item.activity || 'Activity';
      const reason = `Trimmed to meet ${pace} pace limit (${maxActivities} activities max)`;
      log(corrections, 'pace-limit', 'removed', name, '', '', reason);
      // Fix C: surface silent trims to the UI layer.
      day._trimmedForPace.push({
        name,
        time: t.item.time || '',
        reason,
        kind: 'activity'
      });
    });

    day.schedule = day.schedule.filter((_, i) => !trimIndices.has(i));
  }

  // Trim meals if over limit — anchor breakfast (first) and dinner (last), trim from middle
  if (day.meals && day.meals.length > maxMeals) {
    const indexed = day.meals.map((item, i) => ({ item, index: i, priority: getMealPriority(item) }));

    // Protect first (breakfast) and last (dinner) meals — only trim from middle
    if (indexed.length > 2) {
      const first = indexed[0];
      const last = indexed[indexed.length - 1];
      const middle = indexed.slice(1, -1);
      middle.sort((a, b) => a.priority - b.priority);

      const trimCount = day.meals.length - maxMeals;
      const toTrim = middle.slice(0, trimCount);
      const trimIndices = new Set(toTrim.map(t => t.index));

      toTrim.forEach(t => {
        const name = t.item.restaurant || t.item.meal || 'Meal';
        const reason = `Trimmed to meet ${pace} pace limit (${maxMeals} meals max)`;
        log(corrections, 'pace-limit', 'removed', name, '', '', reason);
        // Fix C: surface silent trims.
        day._trimmedForPace.push({
          name,
          time: t.item.time || '',
          reason,
          kind: 'meal'
        });
      });

      day.meals = day.meals.filter((_, i) => !trimIndices.has(i));
    }
  }

  // Fix B: after the trim, check whether the remaining schedule has a large void
  // between meals that the downstream free-time detector will need to fill. We do
  // NOT undo the trim — the user's pace preference wins. (Previously logged an
  // info-level warning here for operator correlation; removed because it spammed
  // the production console for every long-gap day.)
}

// ============================================
// DETECT DAY TRIP DESTINATION
// ============================================

function detectDayTripDestination(day) {
  const baseCity = (day.baseCity || '').toLowerCase();
  for (const item of (day.schedule || [])) {
    const actLower = (item.activity || '').toLowerCase();
    const locLower = (item.location || '').toLowerCase();
    const match = actLower.match(/(?:day trip|excursion|visit)\s+to\s+(\w[\w\s]*)/i) ||
                  locLower.match(/(?:day trip|excursion|visit)\s+to\s+(\w[\w\s]*)/i);
    if (match) {
      const dest = match[1].trim().toLowerCase();
      if (dest !== baseCity) return dest;
    }
  }
  return null;
}

// ============================================
// MAIN: normalizeDay()
// ============================================

/**
 * Normalize a single day's itinerary data through the 8-stage pipeline.
 *
 * @param {Object} dayData - Raw day object with schedule, meals, hotel, baseCity
 * @param {Object} context - Normalization context
 * @param {number} context.dayNumber - 1-based day number
 * @param {number} context.totalDays - Total days in itinerary
 * @param {Object|null} context.expectedHotel - Hotel to enforce for this stop
 * @param {string|null} context.expectedBaseCity - BaseCity to enforce
 * @param {string} context.destination - Trip destination (for regional lookup)
 * @param {boolean} context.isFirstDay
 * @param {boolean} context.isLastDay
 * @param {string|null} context.arrivalTime - Arrival time for day 1
 * @param {string|null} context.departureTime - Departure time for last day
 * @param {string|null} context.previousDayCity
 * @returns {{ day: Object, corrections: Array, warnings: Array, errors: Array }}
 */
export function normalizeDay(dayData, context = {}) {
  // Deep clone to avoid mutating the original
  const day = JSON.parse(JSON.stringify(dayData));
  const corrections = [];
  const warnings = [];
  const errors = [];

  // Stage 0: Normalize meals format (GLM sometimes returns object instead of array)
  if (day.meals && !Array.isArray(day.meals)) {
    if (typeof day.meals === 'object') {
      day.meals = Object.entries(day.meals)
        .filter(([, v]) => v && typeof v === 'object')
        .map(([key, val]) => ({ ...val, type: val.type || key, meal: val.meal || key }));
    } else {
      day.meals = [];
    }
  }

  // Detect day trip destination from schedule
  const dayTripDest = detectDayTripDestination(day);
  const ctx = { ...context, dayTripDestination: dayTripDest || context.dayTripDestination };

  try {
    // Stage 1: Field Completeness
    ensureFieldCompleteness(day, ctx, corrections, warnings);

    // Stage 2: Time Normalization
    normalizeAllTimes(day, ctx, corrections);

    // Stage 3: Duration Normalization
    normalizeAllDurations(day, corrections);

    // Stage 4: Hotel Consistency
    enforceHotelConsistency(day, ctx, corrections);

    // Stage 5: Address Validation
    validateAddresses(day, ctx, corrections, warnings);

    // Stage 6: Geographic Validation
    validateGeography(day, ctx, corrections);

    // Stage 7: Item Filtering
    filterInvalidItems(day, ctx, corrections);

    // Stage 8: Overlap Resolution
    resolveOverlaps(day, corrections);

    // Stage 9: Late-Night Activity Removal
    removeLateNightActivities(day, ctx, corrections);

    // Stage 10: Pace-Based Activity Limits
    enforceActivityLimits(day, ctx, corrections);

    // Stage 11: Start Time Enforcement (runs after trimming so it shifts a clean schedule)
    enforceStartTime(day, ctx, corrections);

    // Stage 12: Re-resolve overlaps after start time shift
    resolveOverlaps(day, corrections);

    // Mark as normalized
    day._normalized = true;

  } catch (err) {
    errors.push({ stage: 'normalizeDay', error: err.message });
  }

  // Per-day structured dumps are only emitted in verbose mode — the itinerary-level
  // summary in normalizeItinerary() is enough for prod. Gate on DEBUG_NORMALIZER.
  if (corrections.length > 0 && isVerboseNormalizer()) {
    console.log(`${PREFIX} Day ${context.dayNumber || '?'}: ${corrections.length} corrections applied`, corrections);
  }

  return { day, corrections, warnings, errors };
}

// ============================================
// MAIN: normalizeItinerary()
// ============================================

/**
 * Normalize an entire itinerary (all days) with cross-day validation.
 *
 * @param {Object} itineraryData - Full itinerary object with .days array
 * @param {Object} context - Normalization context
 * @param {string} context.destination
 * @param {Object} context.travelStruct - Multi-stop travel structure
 * @param {string} context.arrivalTime
 * @param {string} context.departureTime
 * @param {Object} context.hotels - { [stopName]: hotelObj } expected hotels per stop
 * @param {string} context.preferredPace - 'relaxed', 'moderate', 'packed', etc.
 * @param {string} context.preferredStartTime - HH:MM preferred daily start time
 * @param {string[]} [context.interests] - User's declared interest categories
 *   (e.g. ['food','culture']). Used by pace-limit trimming to keep activities
 *   matching the user's interests and drop ones they don't care about.
 * @param {Object} [context.tasteProfile] - Optional taste profile with
 *   `tags`, `cuisines`, and/or `vibes` arrays. String matches against activity
 *   text nudge priority upward during trimming.
 * @returns {{ itinerary: Object, dayResults: Array, crossDayIssues: Array }}
 */
export function normalizeItinerary(itineraryData, context = {}) {
  const days = itineraryData.days || [];
  const dayResults = [];
  const crossDayIssues = [];

  // Phase 1: Normalize each day individually
  for (let i = 0; i < days.length; i++) {
    // B3: Detect overnight arrival — check if interCityTransport for this day is overnight
    const isOvernightArrival = (itineraryData.interCityTransport || []).some(t =>
      t.day === (i + 1) && (t.isOvernight || t.note?.toLowerCase()?.includes('overnight'))
    );

    const dayContext = {
      dayNumber: i + 1,
      totalDays: days.length,
      destination: context.destination || '',
      isFirstDay: i === 0,
      isLastDay: i === days.length - 1,
      isOvernightArrival, // B3: Flag for overnight arrival day
      preferredPace: context.preferredPace || null,
      preferredStartTime: context.preferredStartTime || null,
      // Interest-aware pace trimming: thread through so getActivityPriority()
      // can favor activities matching the user's declared interests/taste.
      interests: Array.isArray(context.interests) ? context.interests : [],
      tasteProfile: context.tasteProfile || null,
      arrivalTime: i === 0 ? context.arrivalTime : null,
      departureTime: i === days.length - 1 ? context.departureTime : null,
      previousDayCity: i > 0 ? days[i - 1].baseCity : null,
      // Hotel: find expected hotel from context or from first day of same stop
      expectedHotel: findExpectedHotel(days, i, context),
      expectedBaseCity: findExpectedBaseCity(days, i, context),
    };

    const result = normalizeDay(days[i], dayContext);
    days[i] = result.day;
    dayResults.push(result);
  }

  // Phase 2: Cross-day validation

  // 2a: City ordering — detect non-contiguous city appearance
  const cityRuns = [];
  let currentRun = null;
  days.forEach((day, idx) => {
    const city = day.baseCity || 'Unknown';
    if (!currentRun || currentRun.city !== city) {
      if (currentRun) cityRuns.push(currentRun);
      currentRun = { city, startDay: idx, endDay: idx, days: [idx] };
    } else {
      currentRun.endDay = idx;
      currentRun.days.push(idx);
    }
  });
  if (currentRun) cityRuns.push(currentRun);

  // Check for non-contiguous city appearances
  const cityOccurrences = {};
  cityRuns.forEach(run => {
    cityOccurrences[run.city] = (cityOccurrences[run.city] || 0) + 1;
  });

  Object.entries(cityOccurrences).forEach(([city, count]) => {
    if (count > 1) {
      crossDayIssues.push({
        type: 'city_ordering',
        city,
        count,
        message: `${city} appears ${count} times non-contiguously. Days should be grouped by city.`
      });

      // Auto-fix: reorder days to group by city
      const cityOrder = [];
      const daysByCity = {};
      days.forEach((day, idx) => {
        const c = day.baseCity || 'Unknown';
        if (!daysByCity[c]) {
          daysByCity[c] = [];
          cityOrder.push(c);
        }
        daysByCity[c].push(day);
      });

      // Rebuild days array
      const reordered = [];
      cityOrder.forEach(c => {
        daysByCity[c].forEach(day => reordered.push(day));
      });

      // Replace days in place
      days.length = 0;
      reordered.forEach(d => days.push(d));

      // Renumber
      days.forEach((day, idx) => { day.day = idx + 1; });

      if (isVerboseNormalizer()) console.log(`${PREFIX} Reordered days to group by city`);
    }
  });

  // Fix day themes missing the day number
  days.forEach((day, idx) => {
    if (day.theme) {
      // Replace "Day in City" with "Day X in City"
      day.theme = day.theme.replace(/^Day\s+in\s+/i, `Day ${idx + 1} in `);
      // Replace "Day - Theme" with "Day X - Theme"
      day.theme = day.theme.replace(/^Day\s*[-–—]\s*/i, `Day ${idx + 1} - `);
      // Replace "Day City" (no "in") with "Day X in City" only if starts with just "Day "
      if (/^Day\s+[A-Z]/.test(day.theme) && !/^Day\s+\d/.test(day.theme)) {
        day.theme = day.theme.replace(/^Day\s+/, `Day ${idx + 1} in `);
      }
    }
  });

  // 2a-ii: Same-hotel → same-city enforcement
  // If consecutive days share the SAME hotel name, they must be at the same location.
  // This catches cases like "Tuscany, Italy" Day 4 → "Fiesole" Day 5 with same hotel,
  // which would otherwise generate spurious checkout→drive→checkin to the same hotel.
  for (let i = 1; i < days.length; i++) {
    const prevHotel = days[i - 1].hotel?.name;
    const currHotel = days[i].hotel?.name;
    if (prevHotel && currHotel && areSameHotel(prevHotel, currHotel)) {
      if (!areSameCity(days[i].baseCity, days[i - 1].baseCity)) {
        // Hotels match but cities don't — unify baseCity to the previous day's city
        // (the previous day is more likely correct since it was set first)
        crossDayIssues.push({
          type: 'same_hotel_different_city',
          day: i + 1,
          message: `Day ${i + 1} has same hotel "${currHotel}" as Day ${i} but different city ("${days[i].baseCity}" vs "${days[i - 1].baseCity}") — unifying`
        });
        days[i].baseCity = days[i - 1].baseCity;
        if (isVerboseNormalizer()) console.log(`${PREFIX} Day ${i + 1}: unified baseCity to "${days[i - 1].baseCity}" (same hotel "${currHotel}")`);
      }
    }
  }

  // 2b: Cross-day hotel consistency check (uses fuzzy city matching)
  for (let i = 1; i < days.length; i++) {
    if (areSameCity(days[i].baseCity, days[i - 1].baseCity) && days[i].hotel?.name !== days[i - 1].hotel?.name) {
      crossDayIssues.push({
        type: 'hotel_inconsistency',
        day: i + 1,
        message: `Day ${i + 1} hotel "${days[i].hotel?.name}" differs from Day ${i} hotel "${days[i - 1].hotel?.name}" at same city`
      });
      // Auto-fix: use previous day's hotel
      days[i].hotel = { ...days[i - 1].hotel };
    }
    // 2c: Normalize baseCity format within same stop (e.g., "Lisbon, Portugal" → "Lisbon" to match)
    if (areSameCity(days[i].baseCity, days[i - 1].baseCity) && days[i].baseCity !== days[i - 1].baseCity) {
      // Use the longer/more complete form as canonical
      const canonical = days[i - 1].baseCity.length >= days[i].baseCity.length ? days[i - 1].baseCity : days[i].baseCity;
      days[i].baseCity = canonical;
      days[i - 1].baseCity = canonical;
    }
  }

  // Phase 3: Flight-airport validation — Bug 3 (build 89).
  // The LLM occasionally hallucinates flights between popular hubs (e.g. SFO
  // → JFK on a Telluride trip). The trip builder already resolved the
  // correct arrival/departure airports into itinerary.arrivalAirport /
  // itinerary.departureAirport (or into _legTransports via user pick), so
  // reject any flight schedule item whose from/to doesn't match those
  // airports for the destination. We prefer rewriting over deletion when
  // we have a good replacement code; otherwise drop the item so the
  // shared timeline can fall back to the legit flight card.
  try {
    const flightFixes = validateFlightAirports(itineraryData, days, context);
    if (flightFixes.length > 0) {
      crossDayIssues.push(...flightFixes);
      if (isVerboseNormalizer()) {
        console.log(`${PREFIX} Flight airport validation: ${flightFixes.length} fixes`);
      }
    }
  } catch (fErr) {
    // Never fail the whole pipeline on a flight-validation bug.
    console.warn(`${PREFIX} Flight validation skipped:`, fErr?.message);
  }

  // Single summary log per pipeline run — quiet in prod, detail under DEBUG_NORMALIZER.
  const totalCorrections = dayResults.reduce((sum, r) => sum + (r.corrections?.length || 0), 0);
  const totalWarnings = dayResults.reduce((sum, r) => sum + (r.warnings?.length || 0), 0);
  if (totalCorrections > 0 || totalWarnings > 0 || crossDayIssues.length > 0) {
    console.log(
      `${PREFIX} Applied ${totalCorrections} corrections across ${days.length} days ` +
      `(warnings: ${totalWarnings}, cross-day issues: ${crossDayIssues.length})`
    );
  }

  return { itinerary: { ...itineraryData, days }, dayResults, crossDayIssues };
}

// ============================================
// FLIGHT AIRPORT VALIDATION
// ============================================
//
// Bug 3 (build 89): LLM-generated flight items ("SFO → JFK" on a Telluride
// trip) hallucinate popular US hubs regardless of the actual trip. The
// trip builder already resolved arrivalAirport / departureAirport on the
// itinerary, and the user's flight picks live in _legTransports — those
// are the source of truth. This function walks every day's schedule,
// finds `type: 'flight'` items whose airport fields don't match either
// authoritative code, and either:
//   1. Rewrites the mismatched code if we have a clear single replacement
//      (e.g. itinerary.arrivalAirport.code), or
//   2. Drops the flight item entirely when there's no safe replacement —
//      the shared timeline's flight card will still render correctly from
//      the itinerary-level airport fields.
function _extractCode(raw) {
  if (!raw || typeof raw !== 'string') return '';
  // Accept both bare codes ("JFK") and labeled strings ("JFK Airport", "New York (JFK)").
  const m = raw.match(/\b([A-Z]{3})\b/);
  return m ? m[1].toUpperCase() : '';
}
function _codesEqual(a, b) {
  if (!a || !b) return false;
  return _extractCode(a) === _extractCode(b);
}
export function validateFlightAirports(itineraryMeta, days, context = {}) {
  const fixes = [];
  if (!Array.isArray(days) || days.length === 0) return fixes;

  // Authoritative codes come from the itinerary itself (set by the trip
  // builder / server-side generation). Fall back to context for the few
  // callers that thread airports through that way.
  const arrivalCode = _extractCode(
    itineraryMeta?.arrivalAirport?.code
    || context?.arrivalAirport?.code
    || '',
  );
  const departureCode = _extractCode(
    itineraryMeta?.departureAirport?.code
    || context?.departureAirport?.code
    || '',
  );
  // Per-leg user picks (web: `_legTransports`, mobile legacy: `_selectedFlights`).
  // Build a set of user-blessed codes so we never strip a flight the user
  // themselves chose.
  const userCodes = new Set();
  const pickedFlights = itineraryMeta?._legTransports || itineraryMeta?._selectedFlights || {};
  for (const leg of Object.values(pickedFlights || {})) {
    if (!leg) continue;
    const f = _extractCode(leg.from || leg.fromAirportCode || leg.fromCode || '');
    const t = _extractCode(leg.to || leg.toAirportCode || leg.toCode || '');
    if (f) userCodes.add(f);
    if (t) userCodes.add(t);
  }
  if (arrivalCode) userCodes.add(arrivalCode);
  if (departureCode) userCodes.add(departureCode);

  // If we have neither a trip-builder airport nor a user-picked flight,
  // we can't meaningfully validate — bail out (no false rewrites).
  if (userCodes.size === 0) return fixes;

  for (let di = 0; di < days.length; di++) {
    const d = days[di];
    if (!d || !Array.isArray(d.schedule)) continue;

    const kept = [];
    for (const it of d.schedule) {
      if (!it || typeof it !== 'object') continue;
      const isFlight = it.type === 'flight' || it.transitMode === 'flight' || it.isArrivalFlightCard;
      if (!isFlight) {
        kept.push(it);
        continue;
      }

      // Extract any airport codes the item claims.
      const claimedCodes = [
        it.airportCode, it.homeAirportCode,
        it.fromCode, it.toCode,
        it.fromAirportCode, it.toAirportCode,
        typeof it.from === 'string' ? it.from : it.from?.airport || it.from?.code,
        typeof it.to === 'string' ? it.to : it.to?.airport || it.to?.code,
      ].map(_extractCode).filter(Boolean);

      // No codes claimed? Let it through — normalization happens elsewhere.
      if (claimedCodes.length === 0) { kept.push(it); continue; }

      // A flight should only appear on the arrival day, the departure day,
      // or an explicit inter-city travel day. If this day is none of those,
      // the LLM hallucinated the flight (e.g. "Day 2 SFO → JFK" on a
      // one-stop Telluride trip) — drop it regardless of airport codes.
      const isArrivalDay = di === 0;
      const isLastDay = di === days.length - 1;
      // Heuristic for inter-city travel days: day has an `interCityTransport`
      // entry OR its baseCity differs from the previous day's baseCity.
      const isInterCityTravelDay = (() => {
        if (Array.isArray(d.interCityTransport) && d.interCityTransport.length > 0) return true;
        const prev = days[di - 1];
        if (!prev) return false;
        const a = (prev.baseCity || '').toLowerCase().split(',')[0].trim();
        const b = (d.baseCity || '').toLowerCase().split(',')[0].trim();
        return a && b && a !== b;
      })();
      const flightIsLegit = isArrivalDay || isLastDay || isInterCityTravelDay;
      if (!flightIsLegit) {
        fixes.push({
          type: 'flight_hallucinated_mid_trip',
          day: di + 1,
          codes: claimedCodes,
          message: `Day ${di + 1}: dropped hallucinated mid-trip flight ${claimedCodes.join(' → ')} — day is not an arrival/departure/inter-city travel day`,
        });
        continue; // drop
      }

      // At least one claimed code must match a user-blessed code.
      const anyMatch = claimedCodes.some((c) => userCodes.has(c));
      if (anyMatch) { kept.push(it); continue; }

      // Hallucinated flight — rewrite if we have a single authoritative
      // code to substitute, otherwise drop. An arrival-direction flight
      // points at arrivalCode; a departure-direction flight points at
      // departureCode. If we can't tell direction, prefer arrivalCode
      // for non-last-day flights, departureCode for last-day flights.
      const targetCode = isLastDay ? (departureCode || arrivalCode) : (arrivalCode || departureCode);
      if (targetCode) {
        const rewritten = { ...it };
        // Best-effort single-field rewrite. Leave unrelated text fields
        // (description, activity title) alone — the shared timeline
        // re-renders flight cards from structural fields.
        if (typeof rewritten.airportCode === 'string') rewritten.airportCode = targetCode;
        if (typeof rewritten.toCode === 'string') rewritten.toCode = targetCode;
        if (typeof rewritten.toAirportCode === 'string') rewritten.toAirportCode = targetCode;
        if (typeof rewritten.to === 'string') rewritten.to = targetCode;
        rewritten._wasRewrittenBy = 'validateFlightAirports';
        rewritten._originalCodes = claimedCodes;
        kept.push(rewritten);
        fixes.push({
          type: 'flight_airport_rewrite',
          day: di + 1,
          from: claimedCodes.join('→'),
          to: targetCode,
          message: `Day ${di + 1}: rewrote hallucinated flight (${claimedCodes.join(' → ')}) to match trip airport ${targetCode}`,
        });
      } else {
        fixes.push({
          type: 'flight_airport_drop',
          day: di + 1,
          codes: claimedCodes,
          message: `Day ${di + 1}: dropped hallucinated flight ${claimedCodes.join(' → ')} — no matching trip airport`,
        });
        // Do NOT push to kept — item is dropped.
      }
    }
    if (kept.length !== d.schedule.length) {
      d.schedule = kept;
    }
  }

  return fixes;
}

/**
 * Recalculate schedule times after a user reorders activities.
 * Maintains the same durations and travel buffers — only shifts times
 * so they flow sequentially from the first item's time.
 *
 * @param {Array} schedule — the day's schedule array (activity items)
 * @param {string} preferredStartTime — fallback start time if first item has none (default '09:00')
 * @returns {Array} new schedule with recalculated times
 */
export function reorderScheduleTimes(schedule, preferredStartTime = '09:00') {
  if (!schedule || schedule.length === 0) return schedule;

  let currentMinutes = parseTimeToMinutes(schedule[0]?.time || preferredStartTime);

  return schedule.map((item, idx) => {
    if (idx === 0) {
      return { ...item }; // Keep first item's time
    }

    const prevItem = schedule[idx - 1];
    const prevDuration = parseDurationToMinutes(prevItem.duration) || 60;
    const buffer = item.type === 'transit' || prevItem.type === 'transit' ? 0 : 15; // 15 min buffer between activities

    currentMinutes = currentMinutes + prevDuration + buffer;

    // Don't push past midnight
    if (currentMinutes >= 1440) currentMinutes = 1380; // 11 PM max

    const hours = Math.floor(currentMinutes / 60);
    const mins = currentMinutes % 60;
    const newTime = `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;

    return { ...item, time: newTime };
  });
}

/**
 * Find the expected hotel for a given day based on travel structure
 */
function findExpectedHotel(days, dayIndex, context) {
  // If explicit hotels mapping provided
  if (context.hotels) {
    const day = days[dayIndex];
    const city = day?.baseCity;
    if (city && context.hotels[city]) return context.hotels[city];
  }

  // Look at previous days at the same city (fuzzy match)
  for (let i = dayIndex - 1; i >= 0; i--) {
    if (areSameCity(days[i].baseCity, days[dayIndex]?.baseCity) && days[i].hotel?.name) {
      return days[i].hotel;
    }
  }
  return null;
}

function findExpectedBaseCity(days, dayIndex, context) {
  // If a previous day has same hotel, use its baseCity
  for (let i = dayIndex - 1; i >= 0; i--) {
    if (days[i].hotel?.name === days[dayIndex]?.hotel?.name && days[i].baseCity) {
      return days[i].baseCity;
    }
  }
  return null;
}
